import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type UIEvent,
} from "react";
import {
  AnimatePresence,
  animate,
  motion,
  useMotionTemplate,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
  type MotionStyle,
  type MotionValue,
  type PanInfo,
} from "motion/react";
import {
  ArrowRight,
  Check,
  ChevronDown,
  Clock3,
  FileText,
  Infinity as InfinityIcon,
  Pencil,
  TriangleAlert,
  X,
} from "lucide-react";
import { cn } from "@/utils/helpers";
import type { MarketSignal } from "@/types/marketSignal.type";
import type { FieldReport } from "@/types/fieldReport.type";
import { scrubVendorTerms } from "@/lib/vendorTerms";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { KitIconButton } from "@/app/routes/dashboard/components/kit/IconButton";
import { citationOf } from "./evidenceDisplay";
import { WindowOptionBody, windowOptions, type WindowOption } from "./AcceptWindowMenu";
import SignalsDialog from "./SignalsDialog";
import {
  acceptWindow,
  calendarDaysUntil,
  formatShortDate,
  kindLabelSingular,
} from "./signalMeta";
import { HOUSE_EASE } from "./signalsMotion";
import EditPendingSignal, { type PendingSignalEdit } from "./EditPendingSignal";
import KindGlyph from "./KindGlyph";
import SourceFavicon from "./SourceFavicon";

type Dir = "accept" | "decline";

/** How long a decision is held, undoable, before it is sent. */
const HOLD_MS = 5000;
/** A release past this share of the card's width commits the swipe. */
const COMMIT_SHARE = 0.3;
/** A fling faster than this (px/s) commits regardless of distance. */
const FLING_VELOCITY = 500;
/** Behind cards (Signals / Deck stack): each is 8 narrower per side, the same
 *  height, and sits 12 then 20 lower, so their feet show under the top card. */
const STEP_X = 8;
const STEP_Y = [0, 12, 20];
const BEHIND = 2;
/** A committed card slides off its side this fast. */
const EXIT_SECONDS = 0.22;
/** The deck never shrinks below this, so a short phone scrolls instead of crushing it. */
const MIN_DECK_HEIGHT = 420;

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function partOfDay(now: Date): "morning" | "afternoon" | "evening" {
  const h = now.getHours();
  if (h < 12) return "morning";
  if (h < 18) return "afternoon";
  return "evening";
}

/** "Saturday 26 September". */
function longDate(now: Date): string {
  return `${WEEKDAYS[now.getDay()]} ${now.getDate()} ${MONTHS[now.getMonth()]}`;
}

function sourcesLabel(n: number): string {
  return `${n} ${n === 1 ? "source" : "sources"}`;
}

function daysPhrase(days: number): string {
  if (days <= 0) return "today";
  return `in ${days} ${days === 1 ? "day" : "days"}`;
}

/** The nearest scrolling ancestor: the cockpit's field, or the harness's. */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let n = el.parentElement; n; n = n.parentElement) {
    const oy = getComputedStyle(n).overflowY;
    if (oy === "auto" || oy === "scroll") return n;
  }
  return null;
}

interface Decision {
  id: string;
  dir: Dir;
  validUntil: string | null;
  /** Accepted as Permanent: graduated into Brand DNA once the accept lands. */
  permanent?: boolean;
}

interface Held extends Decision {
  signal: MarketSignal;
  timer: number;
}

/** How far a drag of `x` px has come towards committing, 0..1, per side. */
function progressOf(x: number, width: number, side: 1 | -1): number {
  // Until the stack has been measured there is no threshold to measure against.
  if (width <= 0) return 0;
  const threshold = Math.max(1, width * COMMIT_SHARE);
  return Math.max(0, Math.min(1, (side * x) / threshold));
}

/* ------------------------------------------------------------------ card */

function DeckCardContent({
  signal,
  canEdit,
  hideHint,
  openedIds,
  isDragging,
  onEdit,
  onOpenSource,
  report,
  onOpenReport,
}: {
  signal: MarketSignal;
  canEdit: boolean;
  /** The field report this signal was filed from, when it is on the page. */
  report: FieldReport | null;
  onOpenReport: () => void;
  /** The Undo toast is using the space above the verdict bar. */
  hideHint: boolean;
  /** Observation ids already opened this session; they carry an "Opened" mark. */
  openedIds: Set<string>;
  /** A drag just ended on the card: the click it leaves behind is not a tap. */
  isDragging: () => boolean;
  onEdit: () => void;
  onOpenSource: (index: number) => void;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [atEnd, setAtEnd] = useState(true);
  const bodyRef = useRef<HTMLDivElement>(null);

  const observations = signal.groundingObservations;
  // The prompt leads with the first source that has a finding.
  const withGist = observations.findIndex((o) => !!citationOf(o).gist);
  const leadIndex = observations.length === 0 ? -1 : Math.max(0, withGist);
  const facts = signal.groundingFactIds.length;
  const win = acceptWindow(signal);
  const conf = signal.confidence;
  const hasConfidence = conf != null && Number.isFinite(conf);
  const proposed = formatShortDate(signal.createdAt);
  const sourcesPart = observations.length > 0 ? sourcesLabel(observations.length) : null;
  const byline =
    signal.sourceRunId === null
      ? `Logged by hand · ${proposed}`
      : ["From the field reporter", proposed, sourcesPart].filter(Boolean).join(" · ");
  const openReport = () => {
    if (!isDragging()) onOpenReport();
  };

  // Whether there is more below the fold, measured as the body resizes.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const ro = new ResizeObserver(() => {
      setAtEnd(body.scrollTop + body.clientHeight >= body.scrollHeight - 8);
    });
    ro.observe(body);
    return () => ro.disconnect();
  }, []);

  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    const body = e.currentTarget;
    setScrolled(body.scrollTop > 160);
    setAtEnd(body.scrollTop + body.clientHeight >= body.scrollHeight - 8);
  };

  const confidence = hasConfidence ? (
    <span className="sig-deck__conf">
      <span className="sig-deck__conf-n">{Math.round((conf ?? 0) * 100)}%</span>
      <span className="sig-deck__conf-l">Confidence</span>
    </span>
  ) : null;

  return (
    <>
      {/* Once the hero scrolls away the card keeps its name pinned. */}
      {scrolled ? (
        <div className="sig-deck__pinned" aria-hidden="true">
          <div className="sig-deck__pinned-row">
            <span>{kindLabelSingular(signal.kind)}</span>
            {hasConfidence ? <span className="sig-deck__pinned-conf">{Math.round((conf ?? 0) * 100)}%</span> : null}
          </div>
          <p className="sig-deck__pinned-title" dir="auto">
            <span className="sig-deck__ink">{signal.title}</span>
          </p>
        </div>
      ) : null}

      <div className="sig-deck__body" ref={bodyRef} onScroll={onScroll}>
        <div className="sig-deck__hero">
          <div className="sig-deck__hero-top">
            <span className="sig-deck__kind">
              <KindGlyph kind={signal.kind} size={15} className="sig-glyph" />
              <span className="sig-kind-label">{kindLabelSingular(signal.kind)}</span>
            </span>
            {confidence}
          </div>
          <div>
            {/* The editor's marks are drawn on this span, a line at a time. */}
            <h3 className="sig-deck__title" dir="auto">
              <span className="sig-deck__ink">{signal.title}</span>
            </h3>
            {/* When the report it was filed from is known, the byline is a way to it. */}
            {report ? (
              <p className="sig-deck__byline">
                <button type="button" className="sig-report-link" onClick={openReport}>
                  <FileText aria-hidden="true" />
                  From the field report of {formatShortDate(report.createdAt)}
                </button>
                {sourcesPart ? ` · ${sourcesPart}` : null}
              </p>
            ) : (
              <p className="sig-deck__byline">{byline}</p>
            )}
          </div>
        </div>

        <p className="sig-deck__reading" dir="auto">
          {signal.body}
        </p>

        {/* The lead source is the prompt (the first with a finding); every source
            follows in the evidence list. Tapping either opens the source sheet. */}
        {leadIndex >= 0 ? (() => {
          const o = observations[leadIndex];
          const c = citationOf(o);
          const finding = c.gist || c.title || "";
          const opened = openedIds.has(o.id);
          return (
            <button
              type="button"
              className="sig-deck__prompt"
              aria-haspopup="dialog"
              onClick={() => {
                if (!isDragging()) onOpenSource(leadIndex);
              }}
            >
              <span className="sig-deck__prompt-label">
                <SourceFavicon host={c.host} size={12} />
                {c.host ?? "Source"} · {formatShortDate(c.observedAt)}
                {opened ? (
                  <span className="sig-deck__opened">
                    <Check aria-hidden="true" />
                    Opened
                  </span>
                ) : null}
              </span>
              {c.title && c.gist ? (
                <span className="sig-deck__prompt-title" dir="auto">
                  {scrubVendorTerms(c.title)}
                </span>
              ) : null}
              {finding ? (
                <span className="sig-deck__finding" dir="auto">
                  {scrubVendorTerms(finding)}
                </span>
              ) : null}
            </button>
          );
        })() : null}
        {observations.length > 0 ? (
          <ul className="sig-deck__evidence">
            {observations.map((o, i) => {
              const c = citationOf(o);
              return (
                <li key={o.id}>
                  <button
                    type="button"
                    className="sig-deck__source"
                    aria-haspopup="dialog"
                    onClick={() => {
                      if (!isDragging()) onOpenSource(i);
                    }}
                  >
                    <span className="sig-deck__source-head">
                      <SourceFavicon host={c.host} size={13} />
                      <span className="sig-deck__source-host">{c.host ?? "Source"}</span>
                      <span className="sig-deck__source-date">{formatShortDate(c.observedAt)}</span>
                    </span>
                    {c.title ? (
                      <span className="sig-deck__source-title" dir="auto">
                        {scrubVendorTerms(c.title)}
                      </span>
                    ) : null}
                    {c.gist ? (
                      <span className="sig-deck__source-gist" dir="auto">
                        {scrubVendorTerms(c.gist)}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
        {observations.length === 0 ? (
          <p className="sig-deck__prompt sig-deck__prompt--none">
            {facts > 0
              ? `Grounded in ${facts} Brand DNA ${facts === 1 ? "fact" : "facts"}`
              : "No sources recorded."}
          </p>
        ) : null}

        <div className="sig-deck__if">
          <span className="sig-deck__if-label">If you accept</span>
          <p className="sig-deck__if-text">{win.sentence}</p>
        </div>

        {canEdit ? (
          <button type="button" className="sig-deck__editlink" onClick={onEdit}>
            <Pencil aria-hidden="true" />
            Edit
          </button>
        ) : null}

        {report ? (
          <button type="button" className="sig-deck__readmore" onClick={openReport}>
            <FileText aria-hidden="true" />
            <span>Read the field report this came from</span>
            <ArrowRight aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {/* The fade the verdict bar sits on, and, while there is more below, a
          line saying what is down there, just above the bar. */}
      <div className="sig-deck__fade" aria-hidden="true">
        {!atEnd && !hideHint ? (
          <span className="sig-deck__hint">
            Show more
            <ChevronDown />
          </span>
        ) : null}
      </div>
    </>
  );
}

/**
 * One card in the stack. Depth 0 is the card being decided: it carries the
 * content and drags on x, translate only, never tilting. As it goes the editor
 * marks its headline: heading right, an accent rule is drawn under each line,
 * left to right; heading left, a strike is drawn through each line, right to
 * left, and the card settles back to 80%. Both are CSS variables fed straight
 * from the drag's motion value, so no React state changes per frame. Depths 1
 * and 2 are bare surfaces behind it, only their lower edges showing. When the
 * top card leaves, the one behind rises into place (its offset and width
 * animate) because it is the same element changing depth.
 */
function DeckCard({
  signal,
  depth,
  width,
  reduced,
  canDecide,
  canEdit,
  editing,
  hasControls,
  hideHint,
  openedIds,
  report,
  onOpenReport,
  dragX,
  onDecide,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onOpenSource,
  busy,
}: {
  signal: MarketSignal;
  depth: number;
  width: number;
  reduced: boolean;
  /** The card may be swiped: the viewer can edit and no source sheet is open. */
  canDecide: boolean;
  canEdit: boolean;
  editing: boolean;
  /** The verdict bar overlays this card's foot, so its body clears it. */
  hasControls: boolean;
  hideHint: boolean;
  openedIds: Set<string>;
  report: FieldReport | null;
  onOpenReport: () => void;
  dragX: MotionValue<number>;
  onDecide: (dir: Dir) => void;
  onStartEdit: () => void;
  onSaveEdit: (patch: PendingSignalEdit) => void;
  onCancelEdit: () => void;
  onOpenSource: (index: number) => void;
  busy: boolean;
}) {
  const x = useMotionValue(0);
  /* A drag that springs back still ends in a click on whatever the finger
     started on; this is how a prompt tells that click from a tap. */
  const dragged = useRef(false);
  // The editor's marks: 0..1 along each line, none at all under reduced motion.
  const acceptMark = useTransform(x, (v) => (reduced ? 0 : progressOf(v, width, 1)));
  const declineMark = useTransform(x, (v) => (reduced ? 0 : progressOf(v, width, -1)));
  const opacity = useTransform(declineMark, [0, 1], [1, 0.8]);
  const cardRef = useRef<HTMLDivElement>(null);
  const isTop = depth === 0;

  // Narrower only: scaleX keeps a card behind at the top card's full height.
  const scale = width > 0 ? (width - 2 * STEP_X * depth) / width : 1 - 0.045 * depth;

  const onDragEnd = (_e: unknown, info: PanInfo) => {
    dragX.set(0);
    const w = cardRef.current?.offsetWidth ?? width;
    const { offset, velocity } = info;
    if (offset.x > w * COMMIT_SHARE || (velocity.x > FLING_VELOCITY && offset.x > 0)) {
      onDecide("accept");
    } else if (offset.x < -w * COMMIT_SHARE || (velocity.x < -FLING_VELOCITY && offset.x < 0)) {
      onDecide("decline");
    } else {
      void animate(x, 0, reduced ? { duration: 0 } : { duration: 0.35, ease: HOUSE_EASE });
    }
  };

  return (
    <motion.div
      ref={cardRef}
      className={cn(
        "sig-deck__card",
        isTop ? "is-top" : "is-behind",
        isTop && hasControls && "has-controls",
      )}
      style={
        {
          x,
          opacity,
          originY: 1,
          zIndex: 10 - depth,
          touchAction: "pan-y",
          "--mark-accept": acceptMark,
          "--mark-decline": declineMark,
        } as MotionStyle
      }
      initial={false}
      animate={{ y: STEP_Y[depth] ?? STEP_Y[STEP_Y.length - 1], scaleX: scale, scaleY: 1 }}
      transition={reduced ? { duration: 0 } : { duration: 0.35, ease: HOUSE_EASE }}
      variants={{
        exit: (c: { dir: number; reduced: boolean }) =>
          c.reduced || c.dir === 0
            ? { opacity: 0, transition: { duration: 0.2 } }
            : {
                x: c.dir * (window.innerWidth + 40),
                transition: { duration: EXIT_SECONDS, ease: HOUSE_EASE },
              },
      }}
      exit="exit"
      drag={isTop && canDecide && !editing ? "x" : false}
      dragDirectionLock
      dragMomentum={false}
      onPointerDownCapture={() => {
        dragged.current = false;
      }}
      onDragStart={() => {
        dragged.current = true;
      }}
      onDrag={(_e, info) => dragX.set(info.offset.x)}
      onDragEnd={onDragEnd}
      aria-hidden={isTop ? undefined : true}
    >
      {isTop ? (
        <>
          <div className="sig-deck__clip">
            {editing ? (
              <div className="sig-deck__edit">
                <EditPendingSignal
                  inDeck
                  signal={signal}
                  busy={busy}
                  onSave={onSaveEdit}
                  onCancel={onCancelEdit}
                />
              </div>
            ) : (
              <DeckCardContent
                signal={signal}
                canEdit={canEdit}
                hideHint={hideHint}
                openedIds={openedIds}
                isDragging={() => dragged.current}
                onEdit={onStartEdit}
                onOpenSource={onOpenSource}
                report={report}
                onOpenReport={onOpenReport}
              />
            )}
          </div>
        </>
      ) : null}
    </motion.div>
  );
}

/* ----------------------------------------------------------- verdict bar */

/**
 * The verdict bar (Signals / Verdict bar, style B): two floating capsules at the
 * card's foot, 48 tall and 12 in from its edges. Decline is frost glass with a
 * red ×, Accept the accent with a ✓.
 *
 * It also answers the swipe. Heading left, Decline fills red from its outer
 * edge by exactly how far the drag has come towards committing; its label is set
 * twice and the inked copy is clipped to the same edge, so it stays legible as
 * the edge crosses it. At the commit point the words become Release to accept or
 * Release to decline, and the other capsule steps back to 45%. A tap on either
 * commits exactly as a swipe does.
 */
function VerdictBar({
  dragX,
  width,
  acceptAria,
  declineAria,
  onDecide,
}: {
  dragX: MotionValue<number>;
  width: number;
  acceptAria: string;
  declineAria: string;
  onDecide: (dir: Dir) => void;
}) {
  const declineProgress = useTransform(dragX, (v) => progressOf(v, width, -1));
  // The inked copy keeps only the stretch the fill has covered, from the same edge.
  const declineUncovered = useTransform(declineProgress, (p) => (1 - p) * 100);
  const declineClip = useMotionTemplate`inset(0 ${declineUncovered}% 0 0)`;
  const acceptDim = useTransform(dragX, [-40, 0], [0.45, 1]);
  const declineDim = useTransform(dragX, [0, 40], [1, 0.45]);

  // Only the crossing into a full measure re-renders, to swap the words.
  const [armed, setArmed] = useState<Dir | null>(null);
  useMotionValueEvent(dragX, "change", (v) => {
    setArmed(
      progressOf(v, width, 1) >= 1 ? "accept" : progressOf(v, width, -1) >= 1 ? "decline" : null,
    );
  });

  const declineText = armed === "decline" ? "Release to decline" : "Decline";
  const acceptText = armed === "accept" ? "Release to accept" : "Accept";

  const declineFace = (
    <>
      <X aria-hidden="true" />
      {declineText}
    </>
  );

  return (
    <div className="sig-verdict">
      <motion.button
        type="button"
        className="sig-verdict__btn sig-verdict__btn--decline"
        style={{ opacity: declineDim }}
        aria-label={declineAria}
        onClick={() => onDecide("decline")}
      >
        <motion.span
          className="sig-verdict__fill"
          style={{ scaleX: declineProgress, originX: 0 }}
          aria-hidden="true"
        />
        <span className="sig-verdict__label" aria-hidden="true">
          {declineFace}
        </span>
        <motion.span
          className="sig-verdict__label sig-verdict__label--ink"
          style={{ clipPath: declineClip }}
          aria-hidden="true"
        >
          {declineFace}
        </motion.span>
      </motion.button>
      <motion.button
        type="button"
        className="sig-verdict__btn sig-verdict__btn--accept"
        style={{ opacity: acceptDim }}
        aria-label={acceptAria}
        onClick={() => onDecide("accept")}
      >
        <span className="sig-verdict__label" aria-hidden="true">
          <Check aria-hidden="true" />
          {acceptText}
        </span>
      </motion.button>
    </div>
  );
}

/* ---------------------------------------------------------- window sheet */

/**
 * How long it steers, on the phone (Signals / Window · Phone): a floating sheet
 * opened from the window line above the verdict bar. The rows are the desk's
 * window menu (AcceptWindowMenu); one is chosen, then Accept decides the card
 * for that window, Permanent included.
 */
function WindowSheet({
  signal,
  options,
  initialKey,
  onAccept,
  onClose,
}: {
  signal: MarketSignal;
  options: WindowOption[];
  initialKey: string;
  onAccept: (key: string) => void;
  onClose: () => void;
}) {
  const [key, setKey] = useState(initialKey);
  const win = acceptWindow(signal);
  const sub = win.stale
    ? `${signal.title} · its suggested window ended on ${formatShortDate(win.staleDate)}, so it counts from today.`
    : win.validUntil
      ? `${signal.title} · suggested to steer until ${formatShortDate(win.validUntil)}.`
      : `${signal.title} · suggested to steer until a newer signal replaces it.`;

  return (
    <SignalsDialog
      labelledBy="sig-window-title"
      onClose={onClose}
      className="sig-dialog__panel--new sig-dialog__panel--window"
    >
      <div className="sig-wsheet">
        <span className="sig-wsheet__grab" aria-hidden="true" />
        <div className="sig-wsheet__head">
          <h2 className="sig-wsheet__title" id="sig-window-title">
            How long it steers
          </h2>
          <KitIconButton icon={X} aria-label="Close" onClick={onClose} />
        </div>
        <p className="sig-wsheet__sub" dir="auto">
          {sub}
        </p>
        <div className="sig-wsheet__opts" role="radiogroup" aria-labelledby="sig-window-title">
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={key === o.key}
              disabled={o.disabled}
              className={cn(
                "sig-wsheet__opt",
                key === o.key && "is-on",
                o.permanent && "is-permanent",
              )}
              onClick={() => setKey(o.key)}
            >
              <WindowOptionBody
                option={o.permanent ? { ...o, note: "Goes straight into Brand DNA as a fact and stops expiring" } : o}
                on={key === o.key}
              />
            </button>
          ))}
        </div>
        <KitButton size="lg" iconLeft={Check} className="sig-wsheet__accept" onClick={() => onAccept(key)}>
          Accept
        </KitButton>
      </div>
    </SignalsDialog>
  );
}

/* ------------------------------------------------------------ done state */

function DoneState({
  now,
  decisions,
  steering,
  held,
  hasReports,
  onSwitchTab,
}: {
  now: Date;
  decisions: Decision[];
  steering: MarketSignal[];
  held: Held | null;
  hasReports: boolean;
  onSwitchTab: (tab: "steering" | "reports") => void;
}) {
  const accepted = decisions.filter((d) => d.dir === "accept");
  const declined = decisions.filter((d) => d.dir === "decline");

  // A held accept is not steering on the server yet, but it is on this page. The
  // count matches the Steering tab: a signal whose window has passed is still
  // steering until the server expires it, it just cannot be "the next to close".
  const steeringNow: { title: string; validUntil: string | null }[] = steering.map((s) => ({
    title: s.title,
    validUntil: s.validUntil,
  }));
  if (held && held.dir === "accept" && !steering.some((s) => s.id === held.id)) {
    steeringNow.push({ title: held.signal.title, validUntil: held.validUntil });
  }

  const ends = accepted.map((d) => (d.validUntil ? formatShortDate(d.validUntil, now) : null));
  const oneEnd = ends.length > 0 && ends.every((e) => e !== null && e === ends[0]) ? ends[0] : null;

  let next: { title: string; days: number } | null = null;
  for (const s of steeringNow) {
    if (!s.validUntil) continue;
    const days = calendarDaysUntil(s.validUntil, now);
    if (days < 0) continue;
    if (!next || days < next.days) next = { title: s.title, days };
  }

  const rows = [
    {
      key: "accepted",
      n: accepted.length,
      label: "Accepted",
      note: oneEnd ? `They steer the work until ${oneEnd}.` : "They steer the work now.",
    },
    {
      key: "declined",
      n: declined.length,
      label: "Declined",
      note: "Filed in History with today's date.",
    },
    {
      key: "steering",
      n: steeringNow.length,
      label: "Steering now",
      note: next
        ? `The next window closes ${daysPhrase(next.days)}: ${next.title}.`
        : "None of them has an end date.",
    },
  ].filter((r) => r.n > 0);

  const title =
    decisions.length === 0 ? "Nothing waiting." : `That's this ${partOfDay(now)}'s signals.`;

  return (
    <div className="sig-deck__done">
      <div>
        <h2 className="sig-deck__done-title">{title}</h2>
        <p className="sig-deck__sub">{longDate(now)}</p>
      </div>

      {rows.length > 0 ? (
        <ul className="sig-deck__tallies">
          {rows.map((r) => (
            <li key={r.key} className="sig-deck__tally">
              <span className="sig-deck__tally-n">{r.n}</span>
              <span className="sig-deck__tally-text">
                <span className="sig-deck__tally-l">{r.label}</span>
                <span className="sig-deck__tally-note" dir="auto">
                  {r.note}
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="sig-deck__done-acts">
        <KitButton
          variant="primary"
          className="sig-deck__done-btn"
          iconRight={ArrowRight}
          onClick={() => onSwitchTab("steering")}
        >
          Open steering
        </KitButton>
        {hasReports ? (
          <KitButton
            variant="secondary"
            className="sig-deck__done-btn"
            onClick={() => onSwitchTab("reports")}
          >
            Read report
          </KitButton>
        ) : null}
      </div>

      <p className="sig-deck__foot">New signals land here as the field reporter files them.</p>
    </div>
  );
}

/* ------------------------------------------------------------------ deck */

/**
 * TriageDeck: the phone's Waiting tab, one decision at a time.
 *
 * Under a greeting row ("Good morning, Emad" and "1 OF 6") and one progress
 * segment per signal, the top card takes all the remaining height; two more
 * peek 12px each below it. The card is dragged right to accept and left to
 * decline (translate only, with the editor's marks on its headline), or decided
 * on the verdict bar across its foot, which also measures the drag, or with
 * ArrowRight / ArrowLeft when the deck has focus (E edits, as does the link
 * inside the card). The card slides off its side and the one behind rises into
 * its place. Tapping a
 * source prompt opens it in the board's source sheet; the card holds still
 * while the sheet is up, and a source read there is marked "Opened".
 *
 * A swipe is easy to get wrong and the API cannot take a decision back, so the
 * deck HOLDS each one for five seconds before sending it: the card leaves at
 * once (the board marks it departed, so counts and progress move), an Undo
 * strip drains for five seconds just above the verdict bar, and only then does the
 * decision go through the board's usual accept / decline with its narrow
 * rollback. The hold is flushed early by the next decision, by leaving the tab
 * or the page (unmount, visibilitychange hidden, pagehide), and cancelled by
 * Undo.
 *
 * The column fills the height left under the header and bar, so the page does
 * not scroll while the deck is showing; the card's own body scrolls instead.
 */
export default function TriageDeck({
  pending,
  steering,
  hasReports,
  viewerName,
  canEdit,
  isBusy,
  reduced,
  onHold,
  onUndo,
  onSend,
  onEdit,
  onOpenSource,
  reportFor,
  onOpenReport,
  sheetOpen,
  openedSourceIds,
  onSwitchTab,
  failed = null,
  onClearFailed,
}: {
  /** The waiting queue as the board shows it (confidence-ranked, minus the departed). */
  pending: MarketSignal[];
  steering: MarketSignal[];
  hasReports: boolean;
  viewerName: string;
  canEdit: boolean;
  isBusy: (id: string) => boolean;
  reduced: boolean;
  /** Take the card out of the queue now; nothing is sent yet. */
  onHold: (signal: MarketSignal) => void;
  /** Put a held card back; nothing is sent. */
  onUndo: (signal: MarketSignal) => void;
  /** The hold is over: send the decision (Permanent: accept, then graduate). */
  onSend: (signal: MarketSignal, dir: Dir, validUntil: string | null, permanent: boolean) => void;
  onEdit: (signal: MarketSignal, patch: PendingSignalEdit) => void;
  /** Open one of the card's sources in the source sheet. */
  onOpenSource: (signal: MarketSignal, index: number) => void;
  /** The field report a signal was filed from (same run), if it is on the page. */
  reportFor: (signal: MarketSignal) => FieldReport | null;
  /** Go to Reports on that report; leaving the tab flushes a held decision. */
  onOpenReport: (signal: MarketSignal) => void;
  /** The source sheet is up: the card holds still until it closes. */
  sheetOpen: boolean;
  /** Observations opened this session, marked "Opened" on their prompts. */
  openedSourceIds: Set<string>;
  onSwitchTab: (tab: "steering" | "reports") => void;
  /** A sent decision the server refused: its card is back, and the toast offers it again. */
  failed?: { signal: MarketSignal; dir: Dir } | null;
  onClearFailed?: () => void;
}) {
  const [now] = useState(() => new Date());
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [held, setHeld] = useState<Held | null>(null);
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  /** The window chosen per card in the window sheet; the suggestion otherwise. */
  const [windowKeys, setWindowKeys] = useState<Map<string, string>>(() => new Map());
  const [windowSheetOpen, setWindowSheetOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [exitDir, setExitDir] = useState(0);
  const [stackWidth, setStackWidth] = useState(0);
  /* Every signal this session has seen: the mount snapshot, then arrivals
     appended in the order they turn up. Grown during render, the way React
     derives state from props, so no effect has to chase the queue. */
  const [sessionIds, setSessionIds] = useState<string[]>(() => pending.map((s) => s.id));
  const newcomers = pending.filter((s) => !sessionIds.includes(s.id)).map((s) => s.id);
  if (newcomers.length > 0) setSessionIds([...sessionIds, ...newcomers]);

  const dragX = useMotionValue(0);

  const deckRef = useRef<HTMLElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const heldRef = useRef<Held | null>(null);
  const onSendRef = useRef(onSend);
  useEffect(() => {
    onSendRef.current = onSend;
  });

  // The pinned card (just undone) returns to the top; the rest keep the board's order.
  const pinned = pinnedId ? pending.find((s) => s.id === pinnedId) : undefined;
  const order = pinned ? [pinned, ...pending.filter((s) => s.id !== pinnedId)] : pending;
  const top = order[0] ?? null;
  const pendingIds = new Set(pending.map((s) => s.id));
  // Decisions whose card came back (Undo, or a failed send) no longer count.
  const madeDecisions = decisions.filter((d) => !pendingIds.has(d.id));
  const decidedCount = sessionIds.filter((id) => !pendingIds.has(id)).length;
  const position = Math.min(sessionIds.length, decidedCount + 1);

  /** Send whatever is held, now. Safe to call from anywhere, any number of times. */
  const flush = useCallback(() => {
    const h = heldRef.current;
    if (!h) return;
    heldRef.current = null;
    window.clearTimeout(h.timer);
    onSendRef.current(h.signal, h.dir, h.validUntil, !!h.permanent);
  }, []);

  // Leaving the page or the tab sends the held decision rather than dropping it.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== "hidden") return;
      flush();
      setHeld(null);
    };
    const onPageHide = () => flush();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      flush();
    };
  }, [flush]);

  // The stack's width sets how much narrower each card behind it is.
  useEffect(() => {
    const el = stackRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setStackWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* Fit the column to the height left under the header and bar, so the page
     itself does not scroll. Written straight to the element: it is layout, not
     state, and it has to follow the viewport and the header as they resize. */
  useEffect(() => {
    const el = deckRef.current;
    if (!el) return;
    const scroller = scrollParent(el);
    if (!scroller) return;
    const fit = () => {
      const top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
      let after = 0;
      for (let n = el.parentElement; n && n !== scroller; n = n.parentElement) {
        const cs = getComputedStyle(n);
        after += parseFloat(cs.paddingBottom) + parseFloat(cs.borderBottomWidth);
      }
      el.style.height = `${Math.max(MIN_DECK_HEIGHT, scroller.clientHeight - top - after)}px`;
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(scroller);
    const head = el.closest(".sig")?.querySelector(".wb-head");
    if (head) ro.observe(head);
    window.addEventListener("resize", fit);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
      el.style.height = "";
    };
  }, []);

  /** Focus the first control of the edit form once it has rendered. */
  const startEdit = (id: string) => {
    setEditingId(id);
    requestAnimationFrame(() => {
      deckRef.current
        ?.querySelector<HTMLElement>(".sig-deck__edit [tabindex='0'], .sig-deck__edit input")
        ?.focus({ preventScroll: true });
    });
  };

  /** The window a card would be accepted for: the one chosen in the sheet, else the suggestion. */
  const windowFor = (signal: MarketSignal, key?: string): WindowOption => {
    const options = windowOptions(signal);
    const chosen = key ?? windowKeys.get(signal.id);
    return options.find((o) => o.key === chosen && !o.disabled) ?? options[0];
  };

  const decide = (signal: MarketSignal, dir: Dir, windowKey?: string) => {
    if (!canEdit) return;
    const wasLast = order.length === 1;
    flush();
    const option = dir === "accept" ? windowFor(signal, windowKey) : null;
    const validUntil = option ? option.validUntil : null;
    const permanent = !!option?.permanent;
    const timer = window.setTimeout(() => {
      flush();
      setHeld(null);
    }, HOLD_MS);
    const next: Held = { id: signal.id, dir, validUntil, permanent, signal, timer };
    heldRef.current = next;
    setHeld(next);
    setExitDir(dir === "accept" ? 1 : -1);
    setDecisions((prev) => [
      ...prev.filter((d) => d.id !== signal.id),
      { id: signal.id, dir, validUntil, permanent },
    ]);
    if (pinnedId === signal.id) setPinnedId(null);
    dragX.set(0);
    onHold(signal);
    // The last decision takes the buttons with it; focus stays on the deck.
    if (wasLast) requestAnimationFrame(() => deckRef.current?.focus({ preventScroll: true }));
  };

  const undo = () => {
    const h = heldRef.current;
    if (!h) return;
    heldRef.current = null;
    window.clearTimeout(h.timer);
    setHeld(null);
    setExitDir(0);
    setDecisions((prev) => prev.filter((d) => d.id !== h.id));
    // The returning card goes back on top, so an edit open on the card it
    // covers would unmount mid-typing and reopen later; close it now.
    setEditingId(null);
    setPinnedId(h.id);
    onUndo(h.signal);
    deckRef.current?.focus({ preventScroll: true });
  };

  const onDeckKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (!top || editingId || sheetOpen || windowSheetOpen || e.altKey || e.ctrlKey || e.metaKey) return;
    const target = e.target as HTMLElement;
    // Arrow keys decide only when the deck itself has focus: on a source prompt,
    // the Edit link or a verdict half they must not accept or decline anything.
    if (target.closest("input, textarea, button, a, [role='button'], .sig-deck__toast")) return;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      decide(top, "accept");
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      decide(top, "decline");
    } else if ((e.key === "e" || e.key === "E") && canEdit) {
      e.preventDefault();
      startEdit(top.id);
    }
  };

  const firstName = viewerName.trim().split(/\s+/)[0] ?? "";
  const greeting = `Good ${partOfDay(now)}${firstName ? `, ${firstName}` : ""}`;
  const topWindow = top ? windowFor(top) : null;
  const editing = !!top && editingId === top.id;
  const allDone = !top;

  const toastText = held
    ? held.dir === "accept"
      ? held.permanent
        ? "Accepted · into Brand DNA as a fact"
        : held.validUntil
          ? `Accepted · steers until ${formatShortDate(held.validUntil, now)}`
          : "Accepted · steers until a newer signal replaces it"
      : "Declined · filed in History"
    : "";

  // The verdict bar shows whenever the viewer can decide and the card is not being edited.
  const controls = canEdit && !editing;

  // Signals / Phone triage / Just accepted, Just declined, Couldn't send: the
  // library Toast (mark, the consequence, its action under it, Close).
  const toast = held ? (
    <div className="sig-deck__toast" role="status">
      {held.dir === "accept" ? <Check aria-hidden="true" /> : <X aria-hidden="true" />}
      <span className="sig-deck__toast-text">
        <span className="sig-deck__toast-msg">{toastText}</span>
        <button type="button" className="sig-deck__undo" onClick={undo}>
          Undo
        </button>
      </span>
      <button
        type="button"
        className="sig-deck__toast-close"
        aria-label="Close"
        onClick={() => {
          flush();
          setHeld(null);
        }}
      >
        <X aria-hidden="true" />
      </button>
    </div>
  ) : failed ? (
    <div className="sig-deck__toast is-error" role="alert">
      <TriangleAlert aria-hidden="true" />
      <span className="sig-deck__toast-text">
        <span className="sig-deck__toast-msg">Couldn’t send your decision</span>
        <button
          type="button"
          className="sig-deck__undo"
          onClick={() => {
            onClearFailed?.();
            decide(failed.signal, failed.dir);
          }}
        >
          Try again
        </button>
      </span>
      <button type="button" className="sig-deck__toast-close" aria-label="Close" onClick={onClearFailed}>
        <X aria-hidden="true" />
      </button>
    </div>
  ) : null;

  return (
    <section
      ref={deckRef}
      className="sig-deck"
      aria-label={
        allDone ? "Waiting signals, all decided" : `Waiting signals, ${position} of ${sessionIds.length}`
      }
      tabIndex={0}
      onKeyDown={onDeckKeyDown}
    >
      {!allDone ? (
        <div className="sig-deck__head">
          <h2 className="sig-deck__hello">{greeting}</h2>
          <span className="sig-deck__count">
            {position} of {sessionIds.length}
          </span>
        </div>
      ) : null}

      <div className="sig-deck__progress" aria-hidden="true">
        {sessionIds.map((id) => (
          <span
            key={id}
            className={cn(
              "sig-deck__seg",
              !pendingIds.has(id) && "is-done",
              top?.id === id && "is-current",
            )}
          />
        ))}
      </div>

      {allDone ? (
        <DoneState
          now={now}
          decisions={madeDecisions}
          steering={steering}
          held={held}
          hasReports={hasReports}
          onSwitchTab={onSwitchTab}
        />
      ) : (
        <div className="sig-deck__stack" ref={stackRef}>
          <AnimatePresence initial={false} custom={{ dir: exitDir, reduced }}>
            {order.slice(0, BEHIND + 1).map((s, depth) => (
              <DeckCard
                key={s.id}
                signal={s}
                depth={depth}
                width={stackWidth}
                reduced={reduced}
                canDecide={canEdit && !sheetOpen && !windowSheetOpen}
                canEdit={canEdit}
                editing={editing && depth === 0}
                hasControls={controls}
                hideHint={!!held}
                openedIds={openedSourceIds}
                dragX={dragX}
                busy={isBusy(s.id)}
                onDecide={(dir) => decide(s, dir)}
                onStartEdit={() => startEdit(s.id)}
                onSaveEdit={(patch) => {
                  onEdit(s, patch);
                  setEditingId(null);
                  deckRef.current?.focus({ preventScroll: true });
                }}
                onCancelEdit={() => {
                  setEditingId(null);
                  deckRef.current?.focus({ preventScroll: true });
                }}
                onOpenSource={(index) => onOpenSource(s, index)}
                report={depth === 0 ? reportFor(s) : null}
                onOpenReport={() => onOpenReport(s)}
              />
            ))}
          </AnimatePresence>

          {/* The verdict bar floats on the card's foot. Above it, the window the
              card would be accepted for, which opens the window sheet; while a
              decision is held, the Undo strip takes that place. */}
          {controls && top && topWindow ? (
            <div className="sig-deck__controls">
              {toast ?? (
                <button
                  type="button"
                  className="sig-deck__window"
                  aria-haspopup="dialog"
                  aria-label={`How long it steers: ${topWindow.label}. Change`}
                  onClick={() => setWindowSheetOpen(true)}
                >
                  {topWindow.permanent ? (
                    <InfinityIcon aria-hidden="true" />
                  ) : (
                    <Clock3 aria-hidden="true" />
                  )}
                  <span className="sig-deck__window-text">
                    {topWindow.permanent
                      ? "Permanent · into Brand DNA"
                      : topWindow.validUntil
                        ? `${topWindow.label} · ${topWindow.note}`
                        : "Until superseded"}
                  </span>
                  <span className="sig-deck__window-change">Change</span>
                </button>
              )}
              <VerdictBar
                dragX={dragX}
                width={stackWidth}
                acceptAria={
                  topWindow.permanent
                    ? `Accept ${top.title} permanently, into Brand DNA`
                    : topWindow.validUntil
                      ? `Accept ${top.title} for ${topWindow.label}`
                      : `Accept ${top.title} until superseded`
                }
                declineAria={`Decline ${top.title}`}
                onDecide={(dir) => decide(top, dir)}
              />
            </div>
          ) : null}
        </div>
      )}

      {/* With no verdict bar to sit above (the queue is done, or a card is
          being edited), the Undo strip sits at the foot of the column instead. */}
      {!controls || !top ? <div className="sig-deck__foot-toast">{toast}</div> : null}

      {windowSheetOpen && top ? (
        <WindowSheet
          signal={top}
          options={windowOptions(top)}
          initialKey={windowFor(top).key}
          onClose={() => setWindowSheetOpen(false)}
          onAccept={(key) => {
            setWindowSheetOpen(false);
            setWindowKeys((prev) => new Map(prev).set(top.id, key));
            decide(top, "accept", key);
          }}
        />
      ) : null}
    </section>
  );
}
